import { Button, Checkbox, Form } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import UsersManagerStore from '../stores/UsersManagerStore';
import { ProjectAssignmentVisualStore } from '../stores';

type Props = {
    store: UsersManagerStore;
    projectStore: ProjectAssignmentVisualStore;
    userId: string | null;
    initialRoles: string[] | null;
    successCallback: () => void;
};

export const UserRoleEditModal: React.FC<Props> = ({ store, projectStore, userId, initialRoles, successCallback }) => {
    const [form] = Form.useForm();
    const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (!store.roles) {
            store.init();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const roles = initialRoles || [];
        setSelectedRoles(roles);
        form.setFieldsValue({ roles });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialRoles]);

    const handleCancel = () => {
        form.resetFields();
        setSelectedRoles([]);
        store.setIsUserRoleEditDialogVisible(false);
    };

    const handleSubmit = () => {
        const projectId = store.selectedProjectId;
        if (!userId || !projectId) {
            return;
        }

        const userGlobalRoles = projectStore?.getUserGlobalRoles(userId);
        const projectRoles = selectedRoles.filter(role => !userGlobalRoles.includes(role));

        store.updateObjectRoleAssignments(userId, projectId, 'project', projectRoles);
        handleCancel();
        successCallback();
    };

    const handleRoleChange = (role: string, checked: boolean) => {
        const newSelectedRoles = checked ? [...selectedRoles, role] : selectedRoles.filter(r => r !== role);
        setSelectedRoles(newSelectedRoles);
    };

    if (!userId) {
        return null;
    }

    const globalRoles = projectStore.getUserGlobalRoles(userId);
    const allAvailableRoles = store.roles.map(r => r.name);

    const allCurrentRoles = [...new Set([...selectedRoles, ...globalRoles])];

    return (
        <Modal
            title="Edit roles"
            closable={false}
            visible={store.isUserRoleEditDialogVisible}
            className="alpha-portal-dialog"
            onCancel={handleCancel}
            centered
            maskClosable={false}
            destroyOnClose
            footer={[
                <Button
                    data-id="object-role-assignments-dialog-cancel"
                    key="back"
                    onClick={handleCancel}
                    size="large"
                    className="light"
                >
                    Cancel
                </Button>,
                <Button
                    data-id="object-role-assignments-dialog-submit"
                    key="submit"
                    type="primary"
                    onClick={handleSubmit}
                    size="large"
                >
                    Save
                </Button>
            ]}
        >
            <Form form={form} layout="vertical" colon={false} data-id="object-role-assignments-form">
                <Form.Item name="roles" label="Roles">
                    <div className="vertical-checkbox-group">
                        {allAvailableRoles.map(role => {
                            const isGlobalRole = globalRoles.includes(role);
                            const isChecked = allCurrentRoles.includes(role);
                            return (
                                <div
                                    key={role}
                                    style={{ marginBottom: '8px' }}
                                    title={
                                        isGlobalRole
                                            ? 'This role is assigned globally and cannot be modified at project level'
                                            : undefined
                                    }
                                >
                                    <Checkbox
                                        checked={isChecked}
                                        disabled={isGlobalRole}
                                        onChange={e => handleRoleChange(role, e.target.checked)}
                                        style={{
                                            color: isGlobalRole ? '#999' : undefined
                                        }}
                                    >
                                        {role}{' '}
                                        {isGlobalRole && (
                                            <span style={{ fontSize: '12px', color: '#999' }}>(Default role)</span>
                                        )}
                                    </Checkbox>
                                </div>
                            );
                        })}
                    </div>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default observer(UserRoleEditModal);
